"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ONBOARDING_ZONE_GAP = exports.onboardingListSchema = exports.onboardingFacilitySchema = exports.onboardingGapSchema = void 0;
exports.summaryScheduleGap = summaryScheduleGap;
/**
 * `RPM-061` — the onboarding checklist: every building of the tenant, what it still needs, one line
 * per failing check, and whether it is ready (`J-295`).
 *
 * EVERY BUILDING OF THE TENANT, ONE WITH NO PROFILE AMONG THEM. `v_facility_readiness` reads FROM
 * `rpm_facility_profiles`, so its `has_profile` is true by construction and a building with no
 * profile is absent from it rather than false (`F-056` gap 3, `CHUNK-8.md` §2). The checklist starts
 * from the buildings instead: a mirror building whose `data_source` is one of the tenant's live
 * platforms (`source_systems.mirror_data_source_value`, `03-RPM-SCHEMA.md` §4.2.1, N2), and every
 * building the tenant has already profiled. A building with no profile lists its time zone as the
 * one thing it still needs; the other four checks are the view's, and the view reads a building
 * only once it has a profile.
 *
 * DRIFT IS COMPUTED WHEN THE LIST IS READ (`sx-zone-drift`, `RPM-061` row 4). The mirror's zone
 * differing from the operative one is raised with its blast radius; nothing changes automatically.
 */
const zod_1 = require("zod");
const rpm_readiness_js_1 = require("./rpm-readiness.js");
const rpm_facility_profile_js_1 = require("./rpm-facility-profile.js");
/** One thing a building still needs: the check, its line in the nurse's words, and where it is fixed. */
exports.onboardingGapSchema = zod_1.z
    .object({
    check: rpm_readiness_js_1.readinessCheckSchema,
    /** One line per failing check; the link's name states the gap (`RPM-061`'s Keyboard row). */
    gap: zod_1.z.string(),
    /** The screen that fixes it. Null for a first time zone, which is set on the checklist itself. */
    fixAt: zod_1.z.string().nullable(),
})
    .meta({ id: "OnboardingGap", description: "One check a building fails, and where it is fixed" });
exports.onboardingFacilitySchema = zod_1.z
    .object({
    facilityId: zod_1.z.string().min(1).max(20),
    name: zod_1.z.string(),
    /** False for a building with no current profile: it has no zone yet. */
    hasProfile: zod_1.z.boolean(),
    /** The operative zone, or null when the building has no profile. */
    operativeZone: zod_1.z.string().nullable(),
    /** What `carease.facilities.timezone` says now. An observation, never an authority. */
    mirrorZone: zod_1.z.string().nullable(),
    /** True when the routing import loaded this building's routing (`RPM-104`). */
    importLinked: zod_1.z.boolean(),
    ready: zod_1.z.boolean(),
    gaps: zod_1.z.array(exports.onboardingGapSchema),
    /** The mirror's zone differing from the operative one, with its blast radius; null when none. */
    drift: rpm_facility_profile_js_1.zoneDriftSchema.nullable(),
})
    .meta({ id: "OnboardingFacility", description: "One building of the onboarding checklist" });
exports.onboardingListSchema = zod_1.z
    .object({ items: zod_1.z.array(exports.onboardingFacilitySchema) })
    .meta({ id: "OnboardingList", description: "Every building of the tenant, with what it needs" });
/**
 * The checklist's line for each check, in the words the nurse meets when an alert runs into it, and
 * the screen that fixes it. Four are readiness's own (`READINESS_GAPS`); the time zone and the
 * summary schedule are worded here, because a building with no profile is set up on the checklist
 * and the schedule's line names the part that is missing (`RPM-061` row 6).
 */
exports.ONBOARDING_ZONE_GAP = {
    gap: "This building has no time zone, so a typed time cannot be read and no reading can be entered here.",
    fixAt: null,
};
/** The summary schedule's line: which part is missing, and what the routing import leaves open. */
function summaryScheduleGap(s) {
    const parts = [];
    if (!s.weeklySet)
        parts.push(s.importLinked
            ? "the weekly summary day and time, which the routing import does not carry"
            : "the weekly summary day and time");
    if (s.needsDaily && !s.dailySet)
        parts.push(s.importLinked
            ? "a daily summary time, because concerning alerts go to the summary and the sign-off confirmed none"
            : "a daily summary time, because concerning alerts go to the summary");
    return parts.length === 0
        ? "This building's summary schedule is incomplete."
        : `This building's summary schedule needs ${parts.join(", and ")}.`;
}
//# sourceMappingURL=rpm-onboarding.js.map